import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { useActivityIsLandingPage } from "@/features/activity";
import { useCurrentPlan } from "@/features/subscription";
import dayjs from "@/utils/date";
import { useApolloClient } from "@apollo/client";
import * as Sentry from "@sentry/react";
import {
  Navigate,
  Outlet,
  Router,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-location";

import LoadingSpinner from "./components/elements/LoadingSpinner";
import { SidebarProvider } from "./components/elements/Sidebar";
import { WeldLoader } from "./components/elements/WeldLoader";
import Center from "./components/elements/layout/Center";
import Container from "./components/elements/layout/Container";
import ContentContainer from "./components/elements/layout/ContentContainer";
import { WorkspaceBanners } from "./components/modules/AppBanners";
import { AppUnknownExceptionViewRenderer } from "./components/modules/Error/AppUnknownExceptionView";
import {
  FreemiumTrialEndedDialog,
  useFreemiumTrialEndedDialogDisclosure,
} from "./components/modules/FreemiumTrialEndedAppBlocker";
import { KBar } from "./components/modules/KBar/KBar";
import MobileHeader from "./components/modules/MobileHeader";
import TitleManager from "./components/modules/TitleManager";
import { NewDataSourceSlideOverContainer } from "./components/modules/new-data-source-slideover";
import { WorkspaceSidebar } from "./components/modules/sidebar/WorkspaceSidebar";
import {
  ViewDataSourceSlideOverContainer,
  useViewDataSourceSearch,
} from "./components/modules/view-data-source-slideover";
import { CheckoutModalRoot } from "./features/billing";
import { PaymentMethodSetupResultPage } from "./features/billing/pages/PaymentMethodSetupResultPage";
import { DatawarehouseMigrationProvider } from "./features/migration";
import { ActiveMigrationOverlay } from "./features/migration/ActiveMigrationOverlay";
import { useNotifications } from "./features/notifications";
import { NPSSurveyDialogContainer } from "./features/nps";
import { useTrackStonlyEvents } from "./features/product-guide";
import { useTrialStartedListener } from "./features/subscription/useTrialStartedListener";
import { useUserIntercom } from "@/features/intercom";
import { MOBILE_SIZE_VIEWPORT_THRESHOLD } from "./hooks/useIsMobileSizeViewport";
import { useMountEffect } from "./hooks/useMountEffect";
import useTrackSessionStarted from "./hooks/useTrackSessionStarted";
import {
  IntegrationsPrefetcher,
  IntegrationsProvider,
  OAuthCallbackPage,
} from "./integrations";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import { dataSourcesRoute } from "./pages/EltSyncs/routes";
import { useFocusMode } from "./pages/ModelTool/hooks/useFocusMode";
import { AiAssistantSidebarProvider } from "./pages/ModelTool/model-generator/assistant";
import { editorRoute } from "./pages/ModelTool/routes";
import { useModelToolStorageMaintenance } from "./pages/ModelTool/storage/local-storage-maintenance";
import { workflowRoute } from "./pages/Orchestration/routes";
import { exportsRoute } from "./pages/ReverseEltSyncs/routes";
import { settingsRoute } from "./pages/Settings/routes";
import setupRoutes from "./pages/Setup/routes";
import { workspacesRoute } from "./pages/WorkspacesPage/routes";
import { DataWarehouseProvider } from "./providers/DataWarehouseProvider";
import { useToast } from "./providers/ToastProvider";
import { useRefetchUserData, useUserAccounts } from "./providers/UserProvider";
import {
  AccountValidator,
  RouteAwareAccountProvider,
  useCurrentAccount,
  findMostRecentAccount,
  useMostRecentAccountId,
  useResetMostRecentAccountId,
} from "./providers/account";
import { getAccountContext } from "./router/getAccountContext";
import { lazyRouteElement } from "./router/lazyRouteElement";
import { location } from "./router/location";
import type { LocationGenerics } from "./router/locationGenerics";
import * as routeHelpers from "./router/routeHelpers";
import { SocketProvider, useSocketEvent } from "./socket/SocketContext";
import { createStorageKey } from "./utils/storage";

const Main = (props: { children: JSX.Element }) => {
  const accounts = useUserAccounts();
  const matchRoute = useMatchRoute();
  // User is in setup flow?
  const isOnSetupRoute = matchRoute({ to: "/setup", fuzzy: true });
  // Users who received an invitation will be redirected to the `/workspaces` route
  const isOnWorkspacesRoute = matchRoute({ to: "/workspaces", fuzzy: true });
  const isOnInvitationCallbackRoute = matchRoute({
    to: "/_/invitation-callback",
    fuzzy: true,
  });

  const toast = useToast();

  useTrackStonlyEvents();

  useMountEffect(() => {
    const search = new URLSearchParams(window.location.search);
    // The GitHub OAuth flow lands here with `?githubInstallError=invalid_state`
    // when the install session expired and the backend lost the account context
    // (so it couldn't redirect to the workspace-scoped connect page). Surface a
    // toast and strip the param so it doesn't retrigger on subsequent
    // redirects/refreshes — `useNavigateToWorkspace()` preserves search params.
    if (search.get("githubInstallError") === "invalid_state") {
      toast(
        "GitHub setup expired",
        "Your GitHub setup session expired. Please try connecting again.",
        "error",
      );
      search.delete("githubInstallError");
      const qs = search.toString();
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`,
      );
    }
  });

  if (
    accounts.length === 0 &&
    !isOnSetupRoute &&
    !isOnWorkspacesRoute &&
    !isOnInvitationCallbackRoute
  ) {
    return <Navigate to="/setup" replace search />;
  }

  return props.children;
};

const RedirectToMostRecentAccount = () => {
  const navigate = useNavigate();
  const accounts = useUserAccounts();

  const mostRecentAccountId = useMostRecentAccountId();
  const resetMostRecentAccountId = useResetMostRecentAccountId();

  useMountEffect(() => {
    if (!mostRecentAccountId) {
      navigate({ to: routeHelpers.workspacesRoute(), search: true });
      return;
    }

    const slug = findMostRecentAccount(accounts, mostRecentAccountId)?.slug;
    if (slug !== undefined) {
      // Deliberately not useNavigateToWorkspace(): its hard navigation exists
      // to flush account-scoped state when switching workspaces, but nothing
      // account-scoped has loaded yet on "/" — a full reload here would just
      // boot the app twice (auth, vendor scripts, user query all rerun).
      navigate({
        to: routeHelpers.workspaceRoute(slug),
        search: true,
        replace: true,
      });
      return;
    }

    resetMostRecentAccountId();
    navigate({ to: routeHelpers.workspacesRoute(), search: true });
  });

  return null;
};

function RedirectActivityToOverview() {
  const account = useCurrentAccount();
  return <Navigate to={`/${account.slug}/overview`} replace search />;
}

function RedirectToLandingPage() {
  const account = useCurrentAccount();
  const landing = useActivityIsLandingPage() ? "overview" : "sources";

  return <Navigate to={`/${account.slug}/${landing}`} replace search />;
}

export default function Index() {
  const client = useApolloClient();
  return (
    <Router<LocationGenerics>
      defaultLoaderMaxAge={0}
      defaultLinkPreloadMaxAge={0}
      defaultPendingMs={400}
      // Only stops the spinner flashing, and a navigation that finishes just past
      // `defaultPendingMs` waits for all of it, so keep it short.
      defaultPendingMinMs={300}
      defaultPendingElement={<PendingElement />}
      location={location}
      routes={[
        ...setupRoutes,
        {
          path: "signup",
          element: <Navigate to="/" replace search />,
        },
        {
          path: "_",
          children: [
            {
              path: "oauth-callback",
              element: <OAuthCallbackPage />,
            },
            {
              path: "invitation-callback",
              element: <AcceptInvitationPage />,
            },
            {
              path: "payment-method-setup-callback",
              element: <PaymentMethodSetupResultPage />,
            },
          ],
        },
        workspacesRoute(client),
        {
          path: ":slug",
          element: (
            <RouteAwareAccountProvider paramName="slug">
              <IntegrationsPrefetcher />
              <AccountValidator>
                <IntegrationsProvider>
                  <DataWarehouseProvider>
                    <SocketProvider>
                      <DatawarehouseMigrationProvider>
                        <WorkspaceDashboard />
                      </DatawarehouseMigrationProvider>
                    </SocketProvider>
                  </DataWarehouseProvider>
                </IntegrationsProvider>
              </AccountValidator>
            </RouteAwareAccountProvider>
          ),
          loader: async ({ params }) => {
            // Role/permissions arrive with CurrentUserWithAccounts (cached), so
            // no account-scoped requests need to happen before page loaders run.
            return getAccountContext(params.slug, client);
          },
          pendingElement: (
            <Center className="fixed inset-0 z-50 bg-gray-100 dark:bg-background">
              <WeldLoader />
            </Center>
          ),
          children: [
            {
              path: "gather", //Added for routing people to home screen if they have bookmarked old gather path
              element: <Navigate to="/" />,
            },
            {
              path: "extract", //Added for routing people to home screen if they have bookmarked old path
              element: <Navigate to="/" />,
            },
            {
              path: "activate", //Added for routing people to home screen if they have bookmarked old path
              element: <Navigate to="/" />,
            },
            {
              path: "exports", //Added for routing people to home screen if they have bookmarked old path
              element: <Navigate to="/" />,
            },
            editorRoute(client),
            {
              element: <DefaultWorkspaceLayout />,
              children: [
                {
                  path: "activity",
                  element: <RedirectActivityToOverview />,
                },
                {
                  path: "overview",
                  meta: { breadcrumb: () => "Overview" },
                  element: lazyRouteElement(() =>
                    import("./pages/Activity/ActivityRoute").then((m) => (
                      <m.default />
                    )),
                  ),
                },
                dataSourcesRoute(client),
                workflowRoute(client),
                exportsRoute(client),
                settingsRoute(client),
                {
                  path: "transform_tool_decision",
                  element: () =>
                    import("./pages/TransformToolDecision/TransformToolDecisionPage").then(
                      (m) => <m.TransformDecisionPage />,
                    ),
                },
                {
                  element: <RedirectToLandingPage />,
                },
              ],
            },
          ],
        },
        {
          element: <RedirectToMostRecentAccount />,
        },
      ]}
    >
      <Main>
        <Outlet />
      </Main>
    </Router>
  );
}

const sidebarStateAtom = atomWithStorage<boolean>(
  createStorageKey("sidebar-state"),
  false,
);

const WorkspaceDashboard = () => {
  const [defaultSidebarCollapseState, setSidebarCollapseState] =
    useAtom(sidebarStateAtom);

  useTrackSessionStarted();

  useUserIntercom();

  const toast = useToast();
  const refetchUserData = useRefetchUserData();
  const { refetch: refetchCurrentPlan } = useCurrentPlan();

  // Poll for notifications every 30 seconds at the workspace level
  useNotifications({
    fetchPolicy: "network-only",
    pollInterval: 30_000,
    skipPollAttempt: () => document.hidden,
  });

  useTrialStartedListener({
    onTrialStarted(trialEndDate) {
      refetchUserData();
      refetchCurrentPlan();
      toast(
        "Trial started",
        "Your trial will end on " + dayjs(trialEndDate).format("LL"),
        "info",
      );
    },
  });

  useSocketEvent("stripe:subscription-plan-changed", {
    onMessage() {
      refetchCurrentPlan();
    },
  });

  const matchRoute = useMatchRoute();
  // The sidebar should be collapsed initially on the editor route
  const isOnEditorRoute =
    matchRoute({ to: "/:slug/editor", fuzzy: true }) !== undefined;

  useModelToolStorageMaintenance();
  return (
    <KBar>
      <SidebarProvider
        isCollapsed={isOnEditorRoute || defaultSidebarCollapseState}
        breakpoint={MOBILE_SIZE_VIEWPORT_THRESHOLD}
        onCollapseChange={setSidebarCollapseState}
      >
        <AiAssistantSidebarProvider>
          <Outlet />
          <WorkspaceSlideOvers />
        </AiAssistantSidebarProvider>
      </SidebarProvider>
    </KBar>
  );
};

function WorkspaceSlideOvers() {
  const viewDataSource = useViewDataSourceSearch();
  return (
    <>
      <NewDataSourceSlideOverContainer />
      <ViewDataSourceSlideOverContainer
        show={viewDataSource?.syncId != null}
        syncId={viewDataSource?.syncId}
      />
      <CheckoutModalRoot />
      <NPSSurveyDialogContainer />
    </>
  );
}

function DefaultWorkspaceLayout() {
  const [isFocusMode] = useFocusMode();
  const freemiumTrialExpiredDisclosure =
    useFreemiumTrialEndedDialogDisclosure();
  return (
    <div className="absolute inset-0 flex flex-col">
      <WorkspaceBanners />
      <div className="relative grow">
        <Container>
          <TitleManager />
          {!isFocusMode && <WorkspaceSidebar />}
          <ContentContainer>
            <MobileHeader />
            <Sentry.ErrorBoundary fallback={AppUnknownExceptionViewRenderer}>
              <ActiveMigrationOverlay />
              <Outlet />
            </Sentry.ErrorBoundary>
          </ContentContainer>
        </Container>
        <FreemiumTrialEndedDialog {...freemiumTrialExpiredDisclosure} />
      </div>
    </div>
  );
}

const PendingElement = () => {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-background">
      <LoadingSpinner />
    </div>
  );
};
